import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, CircleDot, Layers, Settings2, Wind, ShieldHalf, Scissors, Flame, FileEdit } from 'lucide-react';
import { ProjectCombobox } from '@/components/dashboard/ProjectCombobox';

// Helper function to get the next working day (skips weekends)
const getNextWorkingDay = () => {
  const today = new Date();
  let nextDay = new Date(today);
  nextDay.setDate(nextDay.getDate() + 1); // Start with tomorrow
  
  // Skip weekends (Saturday = 6, Sunday = 0)
  while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
    nextDay.setDate(nextDay.getDate() + 1);
  }
  
  return nextDay.toISOString().split('T')[0];
};

const COPPER_PIPE_SIZES = ['3/8"', '1/2"', '5/8"', '3/4"', '1"', '1-1/4"', '1-1/2"', '2-1/8"', '2-5/8"', '3-1/8"', '3-5/8"', '4-1/8"', '5-1/8"', '6-1/8"'];
const IRON_PIPE_SIZES = ['3/8"', '1/2"', '3/4"', '1"', '1-1/4"', '1-1/2"', '2"', '2-1/2"', '3"', '3-1/2"', '4"', '5"', '6"', '8"', '10"', '12"', '14"', '16"', '18"', '20"', '22"', '24"'];
const INSULATION_THICKNESS = ['1/2"', '1"', '1-1/2"', '2"', '3"'];
const PVC_NUMBERS = Array.from({length: 40}, (_, i) => `#${i + 2}`);
const DUCT_THICKNESS = ['1"', '1.5"', '2"', '3"'];
const PIN_SIZES = ['1"', '1.5"', '2"', '3"'];
const VICTAULIC_SIZES = ['3/4"', '1"', '1-1/4"', '1-1/2"', '2"', '2-1/2"', '3"', '3-1/2"', '4"', '5"', '6"', '8"', '10"', '12"', '14"', '16"', '18"', '20"'];
const COUPLING_COVER_SIZES = ['5', '6', '7', '8', '9', '10', '12', '13', '14', '15', '16', '17', '18', '20', '22', '24'];

export default function MaterialOrderForm({ 
  projects, 
  onSubmit, 
  onCancel,
  mode = 'order', // 'order', 'return', 'transfer'
  hideProjectSelect = false,
  customOrderTypes = null,
  defaultOrderType = 'delivery',
  selectedToProject = null,
  fromProject = null,
  submitButtonText = 'Create Order',
  defaultProjectId = ''
}) {
  const [orderData, setOrderData] = useState({
    project_id: defaultProjectId || '',
    order_type: customOrderTypes ? customOrderTypes[0].value : defaultOrderType,
    delivery_date: mode === 'return' ? new Date().toISOString().split('T')[0] : getNextWorkingDay(),
    notes: ''
  });

  // Pipe Insulation State
  const [pipeItems, setPipeItems] = useState([
    { pipe_type: 'copper', size: '', thickness: '', quantity_ft: '', quantity_pieces: 0 }
  ]);

  // PVC Sheets State
  const [pvcSheets, setPvcSheets] = useState([
    { pvc_number: '', quantity: '' }
  ]);

  // PVC Roll State
  const [pvcRolls, setPvcRolls] = useState([
    { rolls: '' }
  ]);

  const PVC_ROLL_OPTIONS = Array.from({length: 50}, (_, i) => i + 1);

  // PVC Fittings State
  const [pvcFittings, setPvcFittings] = useState({
    elbow_90: [{ pvc_number: '', quantity: '' }],
    elbow_45: [{ pvc_number: '', quantity: '' }],
    end_cap: [{ pvc_number: '', quantity: '' }],
    tee: [{ pvc_number: '', quantity: '' }],
    victaulic_90: [{ size: '', quantity: '' }],
    victaulic_45: [{ size: '', quantity: '' }],
    victaulic_tee: [{ size: '', quantity: '' }],
    coupling_covers: [{ size: '', quantity: '' }]
  });

  // Duct Insulation State
  const [ductItems, setDuctItems] = useState([
    { duct_type: 'flexible_wrap', thickness: '1"', quantity: 1 }
  ]);

  // Jacketing State
  const [jacketingItems, setJacketingItems] = useState([
    { type: '', rolls: '' }
  ]);

  const JACKETING_TYPES = [
    'Venture Clad - Small Roll',
    'Venture Clad - Big Roll', 
    'Aluminum',
    'Flex-Clad',
    'Canvas',
    'Steel'
  ];
  
  const ROLL_OPTIONS = Array.from({length: 50}, (_, i) => i + 1);

  // Aluminum Pipe Jacket State
  const [aluminumPipeJackets, setAluminumPipeJackets] = useState([
    { pipeSize: '', thickness: '', notes: '' }
  ]);

  const ALUMINUM_PIPE_SIZES = [
    '1/2"', '3/4"', '1"', '1-1/4"', '1-1/2"', '2"', '2-1/2"', '3"', 
    '3-1/2"', '4"', '4-1/2"', '5"', '6"', '7"', '8"', '9"', '10"', 
    '12"', '14"', '15"', '16"', '17"', '18"', '19"', '20"'
  ];

  const ALUMINUM_PIPE_THICKNESS = [
    '1"', '1-1/2"', '2"', '2-1/2"', '3"', '4"', '4-1/2"', '5"', '6"'
  ];

  // Tape State
  const [tapeItems, setTapeItems] = useState({
    silver_tape: { quantity: 0 },
    aerp_tape: { quantity: 0 },
    vinyl_white_tape: { quantity: 0 },
    cup_head_pins: [{ size: '1"', quantity: 0 }],
    corner_beads: { quantity: 0 },
    canvas_lagging: { quantity: 0 },
    rubber_glue: { quantity: 0 },
    silicon_sealant: { quantity: 0 }
  });

  // Firestopping State
  const [firestoppingItems, setFirestoppingItems] = useState({
    self_level_silicon: { type: 'Tubes', quantity: 0, comments: '' },
    wall_silicon: { type: 'Tubes', quantity: 0, comments: '' },
    roxul_bag: { quantity: 0, comments: '' },
    collar: [{ size: '1.5"', quantity: 0, comments: '' }],
    wrap_strip: { quantity: 0, comments: '' },
    metacaulk_joint_strip: { quantity: 0, comments: '' },
    metal_strip: { quantity: 0, comments: '' },
    screw: { quantity: 0, comments: '' },
    z_frame: { quantity: 0, comments: '' }
  });

  // Others
  const [othersNotes, setOthersNotes] = useState('');
  const [wizardStep, setWizardStep] = useState(1); // 1: Order Details, 2: Select Category, 3: Add Materials, 4: Review
  const [activeMaterialCategory, setActiveMaterialCategory] = useState('pipe');

  const calculatePieces = (feet) => {
    if (!feet || feet <= 0) return 0;
    return Math.round(parseFloat(feet) / 3);
  };

  const handlePipeChange = (index, field, value) => {
    const updated = [...pipeItems];
    updated[index][field] = value;
    if (field === 'quantity_ft') {
      updated[index].quantity_pieces = calculatePieces(value);
    }
    setPipeItems(updated);
  };

  const addPipeLine = () => {
    if (pipeItems.length < 50) {
      setPipeItems([...pipeItems, { pipe_type: 'copper', size: '', thickness: '', quantity_ft: '', quantity_pieces: 0 }]);
    }
  };

  const removePipeLine = (index) => {
    setPipeItems(pipeItems.filter((_, i) => i !== index));
  };

  const addPVCSheet = () => {
    if (pvcSheets.length < 50) {
      setPvcSheets([...pvcSheets, { pvc_number: '', quantity: '' }]);
    }
  };

  const removePVCSheet = (index) => {
    setPvcSheets(pvcSheets.filter((_, i) => i !== index));
  };

  const addPVCRoll = () => {
    if (pvcRolls.length < 20) {
      setPvcRolls([...pvcRolls, { rolls: '' }]);
    }
  };

  const removePVCRoll = (index) => {
    setPvcRolls(pvcRolls.filter((_, i) => i !== index));
  };

  const updatePVCRoll = (index, value) => {
    const updated = [...pvcRolls];
    updated[index].rolls = value;
    setPvcRolls(updated);
  };

  const addFittingLine = (type) => {
    const updated = { ...pvcFittings };
    updated[type] = [...updated[type], type.includes('victaulic') ? { size: '', quantity: '' } : { pvc_number: '', quantity: '' }];
    setPvcFittings(updated);
  };

  const removeFittingLine = (type, index) => {
    const updated = { ...pvcFittings };
    updated[type] = updated[type].filter((_, i) => i !== index);
    setPvcFittings(updated);
  };

  const updateFittingLine = (type, index, field, value) => {
    const updated = { ...pvcFittings };
    updated[type][index][field] = value;
    setPvcFittings(updated);
  };

  const addDuctLine = () => {
    setDuctItems([...ductItems, { duct_type: 'flexible_wrap', thickness: '1"', quantity: 1 }]);
  };

  const removeDuctLine = (index) => {
    setDuctItems(ductItems.filter((_, i) => i !== index));
  };

  const updateDuctLine = (index, field, value) => {
    const updated = [...ductItems];
    updated[index][field] = value;
    setDuctItems(updated);
  };

  const addJacketingLine = () => {
    if (jacketingItems.length < 20) {
      setJacketingItems([...jacketingItems, { type: '', rolls: '' }]);
    }
  };

  const removeJacketingLine = (index) => {
    setJacketingItems(jacketingItems.filter((_, i) => i !== index));
  };

  const updateJacketingLine = (index, field, value) => {
    const updated = [...jacketingItems];
    updated[index][field] = value;
    setJacketingItems(updated);
  };

  const addAluminumPipeJacketLine = () => {
    if (aluminumPipeJackets.length < 20) {
      setAluminumPipeJackets([...aluminumPipeJackets, { pipeSize: '', thickness: '', notes: '' }]);
    }
  };

  const removeAluminumPipeJacketLine = (index) => {
    setAluminumPipeJackets(aluminumPipeJackets.filter((_, i) => i !== index));
  };

  const updateAluminumPipeJacketLine = (index, field, value) => {
    const updated = [...aluminumPipeJackets];
    updated[index][field] = value;
    setAluminumPipeJackets(updated);
  };

  const handleSubmitOrder = () => {
    const insulation_items = {
      pipe_insulation: pipeItems.filter(item => item.size && item.quantity_ft > 0),
      pvc_sheets: pvcSheets.filter(item => item.pvc_number && item.quantity > 0),
      pvc_rolls: pvcRolls.filter(item => item.rolls > 0),
      pvc_fittings: {
        elbow_90: pvcFittings.elbow_90.filter(item => item.pvc_number && item.quantity > 0),
        elbow_45: pvcFittings.elbow_45.filter(item => item.pvc_number && item.quantity > 0),
        end_cap: pvcFittings.end_cap.filter(item => item.pvc_number && item.quantity > 0),
        tee: pvcFittings.tee.filter(item => item.pvc_number && item.quantity > 0),
        victaulic_90: pvcFittings.victaulic_90.filter(item => item.size && item.quantity > 0),
        victaulic_45: pvcFittings.victaulic_45.filter(item => item.size && item.quantity > 0),
        victaulic_tee: pvcFittings.victaulic_tee.filter(item => item.size && item.quantity > 0),
        coupling_covers: pvcFittings.coupling_covers.filter(item => item.size && item.quantity > 0)
      },
      duct_insulation: ductItems.filter(item => item.thickness && item.quantity > 0),
      jacketing_items: jacketingItems.filter(item => item.type && item.rolls > 0),
      aluminum_pipe_jackets: aluminumPipeJackets.filter(item => item.pipeSize && item.thickness),
      tape_items: {
        silver_tape: tapeItems.silver_tape.quantity > 0 ? tapeItems.silver_tape.quantity : null,
        aerp_tape: tapeItems.aerp_tape.quantity > 0 ? tapeItems.aerp_tape.quantity : null,
        vinyl_white_tape: tapeItems.vinyl_white_tape.quantity > 0 ? tapeItems.vinyl_white_tape.quantity : null,
        cup_head_pins: tapeItems.cup_head_pins.filter(pin => pin.size && pin.quantity > 0),
        corner_beads: tapeItems.corner_beads.quantity > 0 ? tapeItems.corner_beads.quantity : null,
        canvas_lagging: tapeItems.canvas_lagging.quantity > 0 ? tapeItems.canvas_lagging.quantity : null,
        rubber_glue: tapeItems.rubber_glue.quantity > 0 ? tapeItems.rubber_glue.quantity : null,
        silicon_sealant: tapeItems.silicon_sealant.quantity > 0 ? tapeItems.silicon_sealant.quantity : null
      },
      firestopping_items: {
        self_level_silicon: firestoppingItems.self_level_silicon.quantity > 0 ? firestoppingItems.self_level_silicon : null,
        wall_silicon: firestoppingItems.wall_silicon.quantity > 0 ? firestoppingItems.wall_silicon : null,
        roxul_bag: firestoppingItems.roxul_bag.quantity > 0 ? firestoppingItems.roxul_bag : null,
        collar: firestoppingItems.collar.filter(item => item.size && item.quantity > 0),
        wrap_strip: firestoppingItems.wrap_strip.quantity > 0 ? firestoppingItems.wrap_strip : null,
        metacaulk_joint_strip: firestoppingItems.metacaulk_joint_strip.quantity > 0 ? firestoppingItems.metacaulk_joint_strip : null,
        metal_strip: firestoppingItems.metal_strip.quantity > 0 ? firestoppingItems.metal_strip : null,
        screw: firestoppingItems.screw.quantity > 0 ? firestoppingItems.screw : null,
        z_frame: firestoppingItems.z_frame.quantity > 0 ? firestoppingItems.z_frame : null
      },
      others_notes: othersNotes
    };

    onSubmit({
      ...orderData,
      insulation_items
    });
  };

  const selectedProject = projects.find(p => p.id === orderData.project_id);

  // Count items per category for badges
  const getCategoryCounts = () => ({
    pipe: pipeItems.filter(i => i.size && i.quantity_ft > 0).length,
    pvc: pvcSheets.filter(i => i.pvc_number && i.quantity > 0).length + pvcRolls.filter(i => i.rolls > 0).length,
    fittings: Object.values(pvcFittings).flat().filter(i => (i.pvc_number || i.size) && i.quantity > 0).length,
    duct: ductItems.filter(i => i.thickness && i.quantity > 0).length,
    jacketing: jacketingItems.filter(i => i.type && i.rolls > 0).length + aluminumPipeJackets.filter(i => i.pipeSize && i.thickness).length,
    tape: Object.entries(tapeItems).reduce((c, [k, v]) => c + (Array.isArray(v) ? v.filter(i => i.quantity > 0).length : (v.quantity > 0 ? 1 : 0)), 0),
    firestopping: Object.entries(firestoppingItems).reduce((c, [k, v]) => c + (Array.isArray(v) ? v.filter(i => i.quantity > 0).length : (v.quantity > 0 ? 1 : 0)), 0),
    other: othersNotes.trim() ? 1 : 0,
  });

  const totalItemCount = Object.values(getCategoryCounts()).reduce((a, b) => a + b, 0);

  const materialCategories = [
    { key: 'pipe', label: 'Pipe Insulation', icon: CircleDot, color: '#3b82f6' },
    { key: 'pvc', label: 'PVC Cladding', icon: Layers, color: '#8b5cf6' },
    { key: 'fittings', label: 'PVC Fittings', icon: Settings2, color: '#06b6d4' },
    { key: 'duct', label: 'Duct', icon: Wind, color: '#10b981' },
    { key: 'jacketing', label: 'Jacketing', icon: ShieldHalf, color: '#f59e0b' },
    { key: 'tape', label: 'Tape', icon: Scissors, color: '#ec4899' },
    { key: 'firestopping', label: 'Firestopping', icon: Flame, color: '#ef4444' },
    { key: 'other', label: 'Other', icon: FileEdit, color: '#6b7280' },
  ];

  return (
    <div className="material-order-form" data-testid="material-order-form">
      {/* Wizard Progress Bar */}
      <div className="wizard-progress" style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        {[
          { step: 1, label: 'Details' },
          { step: 2, label: 'Category' },
          { step: 3, label: 'Materials' },
          { step: 4, label: 'Review' }
        ].map(({ step, label }) => (
          <button
            key={step}
            onClick={() => {
              if (step === 1 || (step === 2) || (step === 3 && activeMaterialCategory) || (step === 4 && totalItemCount > 0)) {
                setWizardStep(step);
              }
            }}
            style={{
              flex: 1, padding: '8px 4px', borderRadius: '6px', border: 'none', cursor: 'pointer',
              background: wizardStep === step ? '#5b63d3' : wizardStep > step ? '#16a34a' : '#e2e8f0',
              color: wizardStep >= step ? 'white' : '#64748b', fontSize: '0.75rem', fontWeight: 600,
              transition: 'all 0.2s'
            }}
            data-testid={`wizard-step-${step}`}
          >
            {step}. {label}
          </button>
        ))}
      </div>

      {/* ====== STEP 1: Order Details ====== */}
      {wizardStep === 1 && (
        <div className="order-header">
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px' }}>Order Details</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="form-field">
              <Label>Order Type *</Label>
              <Select value={orderData.order_type} onValueChange={(value) => setOrderData({...orderData, order_type: value})}>
                <SelectTrigger data-testid="order-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {customOrderTypes ? (
                    customOrderTypes.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))
                  ) : (
                    <>
                      <SelectItem value="delivery">Delivery</SelectItem>
                      <SelectItem value="pickup">Pick up</SelectItem>
                      <SelectItem value="cage_pickup">Cage Pick Up</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {!hideProjectSelect && (
              <div className="form-field">
                <Label>Job Name *</Label>
                <ProjectCombobox
                  projects={projects}
                  value={orderData.project_id}
                  onChange={(value) => setOrderData({...orderData, project_id: value})}
                  placeholder="Select project"
                  filterActive={false}
                  testId="job-name"
                />
              </div>
            )}

            <div className="form-field">
              <Label>Job Address</Label>
              <Input value={selectedToProject?.address || selectedProject?.address || 'Auto-filled from project'} disabled />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-field">
                <Label>Order Date</Label>
                <Input type="date" value={new Date().toISOString().split('T')[0]} disabled />
              </div>
              <div className="form-field">
                <Label>Order Time</Label>
                <Input type="time" value={new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })} disabled />
              </div>
            </div>

            <div className="form-field">
              <Label>Delivery Date</Label>
              <Input type="date" value={orderData.delivery_date} onChange={(e) => setOrderData({...orderData, delivery_date: e.target.value})} data-testid="delivery-date-input" />
            </div>

            <div className="form-field">
              <Label>Notes</Label>
              <Textarea placeholder="Add any special instructions..." value={orderData.notes} onChange={(e) => setOrderData({...orderData, notes: e.target.value})} data-testid="order-notes" />
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: '16px' }}>
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button onClick={() => setWizardStep(2)} disabled={hideProjectSelect ? false : !orderData.project_id} data-testid="wizard-next-1">
              Next: Select Materials →
            </Button>
          </div>
        </div>
      )}

      {/* ====== STEP 2: Select Material Category ====== */}
      {wizardStep === 2 && (
        <div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '4px' }}>Select Material Category</h3>
          <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '16px' }}>Tap a category to add materials. Green badge = items added.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            {materialCategories.map(cat => {
              const count = getCategoryCounts()[cat.key];
              const IconComp = cat.icon;
              return (
                <button
                  key={cat.key}
                  onClick={() => { setActiveMaterialCategory(cat.key); setWizardStep(3); }}
                  style={{
                    padding: '20px 12px', borderRadius: '12px', border: count > 0 ? '2px solid #16a34a' : '2px solid #e2e8f0',
                    background: count > 0 ? '#f0fdf4' : '#fff', cursor: 'pointer', textAlign: 'center',
                    transition: 'all 0.2s', position: 'relative'
                  }}
                  data-testid={`category-${cat.key}`}
                >
                  <div style={{ marginBottom: '4px', display: 'flex', justifyContent: 'center' }}>
                    <IconComp className="w-8 h-8" style={{ color: cat.color }} />
                  </div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{cat.label}</div>
                  {count > 0 && (
                    <span style={{
                      position: 'absolute', top: '-6px', right: '-6px', background: '#16a34a', color: 'white',
                      borderRadius: '50%', width: '22px', height: '22px', fontSize: '0.7rem', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="form-actions" style={{ marginTop: '16px' }}>
            <Button variant="outline" onClick={() => setWizardStep(1)}>← Back</Button>
            {totalItemCount > 0 && (
              <Button onClick={() => setWizardStep(4)} data-testid="wizard-next-2">
                Review Order ({totalItemCount} items) →
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ====== STEP 3: Add Materials for Selected Category ====== */}
      {wizardStep === 3 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {(() => { const cat = materialCategories.find(c => c.key === activeMaterialCategory); const IconComp = cat?.icon; return IconComp ? <IconComp className="w-5 h-5" style={{ color: cat.color }} /> : null; })()}
              {materialCategories.find(c => c.key === activeMaterialCategory)?.label}
            </h3>
          </div>

          {/* Inline tab content for the selected category - reuses existing tab JSX */}
          <Tabs value={activeMaterialCategory} onValueChange={setActiveMaterialCategory} className="material-tabs">
            <div className="overflow-x-auto hidden sm:block" style={{ WebkitOverflowScrolling: 'touch', marginBottom: '8px' }}>
              <TabsList className="flex gap-1 w-max" style={{ minWidth: 'max-content' }}>
                {materialCategories.map(cat => {
                  const count = getCategoryCounts()[cat.key];
                  return (
                    <TabsTrigger key={cat.key} value={cat.key} style={{ fontSize: '0.75rem', padding: '4px 8px', position: 'relative' }}>
                      {cat.label}
                      {count > 0 && <span style={{ marginLeft: '4px', background: '#16a34a', color: 'white', borderRadius: '50%', width: '16px', height: '16px', fontSize: '0.6rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{count}</span>}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>

        {/* Pipe Insulation Tab */}
        <TabsContent value="pipe">
          <div className="tab-content">
            <div className="tab-header">
              <h3>Pipe Insulation</h3>
              <Button onClick={addPipeLine} disabled={pipeItems.length >= 50} data-testid="add-pipe-line">
                <Plus className="w-4 h-4 mr-2" />
                Add Line ({pipeItems.length}/50)
              </Button>
            </div>

            {pipeItems.length === 0 ? (
              <p className="empty-state">No pipe insulation added yet</p>
            ) : (
              <div className="material-items-list">
                {pipeItems.map((item, index) => (
                  <div key={index} className="material-item-box" data-testid={`pipe-item-${index}`}>
                    <div className="item-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <Select value={item.pipe_type} onValueChange={(value) => handlePipeChange(index, 'pipe_type', value)}>
                        <SelectTrigger className="w-full sm:w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="copper">Copper Pipe</SelectItem>
                          <SelectItem value="iron">Iron Pipe</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={item.size} onValueChange={(value) => handlePipeChange(index, 'size', value)}>
                        <SelectTrigger className="w-full sm:w-32">
                          <SelectValue placeholder="Size" />
                        </SelectTrigger>
                        <SelectContent>
                          {(item.pipe_type === 'copper' ? COPPER_PIPE_SIZES : IRON_PIPE_SIZES).map(size => (
                            <SelectItem key={size} value={size}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select value={item.thickness} onValueChange={(value) => handlePipeChange(index, 'thickness', value)}>
                        <SelectTrigger className="w-full sm:w-32">
                          <SelectValue placeholder="Thickness" />
                        </SelectTrigger>
                        <SelectContent>
                          {INSULATION_THICKNESS.map(thick => (
                            <SelectItem key={thick} value={thick}>{thick}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Input 
                          type="number" 
                          min="1"
                          step="1"
                          placeholder="Qty" 
                          value={item.quantity_ft}
                          onChange={(e) => handlePipeChange(index, 'quantity_ft', e.target.value)}
                          className="w-20 sm:w-24"
                        />
                        <span className="unit-label">ft</span>
                      </div>

                      <Input 
                        value={`${item.quantity_pieces} pieces`}
                        disabled
                        className="pieces-display w-32"
                      />

                      {pipeItems.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removePipeLine(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* PVC Tab */}
        <TabsContent value="pvc">
          <div className="tab-content">
            <div className="tab-header">
              <h3>PVC Sheets</h3>
              <Button onClick={addPVCSheet} disabled={pvcSheets.length >= 50}>
                <Plus className="w-4 h-4 mr-2" />
                Add Line ({pvcSheets.length}/50)
              </Button>
            </div>

            <div className="material-items-list">
              {pvcSheets.map((item, index) => (
                <div key={index} className="material-item-box">
                  <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Select value={item.pvc_number} onValueChange={(value) => {
                      const updated = [...pvcSheets];
                      updated[index].pvc_number = value;
                      setPvcSheets(updated);
                    }}>
                      <SelectTrigger className="w-full sm:w-40">
                        <SelectValue placeholder="PVC #" />
                      </SelectTrigger>
                      <SelectContent>
                        {PVC_NUMBERS.map(num => (
                          <SelectItem key={num} value={num}>{num}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Input 
                        type="number" 
                        min="1"
                        step="1"
                        placeholder="Qty" 
                        value={item.quantity}
                        onChange={(e) => {
                          const updated = [...pvcSheets];
                          updated[index].quantity = e.target.value;
                          setPvcSheets(updated);
                        }}
                        className="w-20 sm:w-24"
                      />
                      <span className="unit-label">Sheets</span>
                    </div>

                    {pvcSheets.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removePVCSheet(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* PVC Roll Section */}
            <div className="tab-header mt-6">
              <h3>PVC Roll</h3>
              <Button onClick={addPVCRoll} disabled={pvcRolls.length >= 20}>
                <Plus className="w-4 h-4 mr-2" />
                Add Line ({pvcRolls.length}/20)
              </Button>
            </div>

            <div className="items-list">
              {pvcRolls.map((item, index) => (
                <div key={index} className="item-row">
                  <Select value={item.rolls} onValueChange={(value) => updatePVCRoll(index, value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="No. of Rolls" />
                    </SelectTrigger>
                    <SelectContent>
                      {PVC_ROLL_OPTIONS.map(num => (
                        <SelectItem key={num} value={num.toString()}>{num} Rolls</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {pvcRolls.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removePVCRoll(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Fittings Tab */}
        <TabsContent value="fittings">
          <div className="tab-content fittings-content">
            {/* 90° Elbow */}
            <div className="fitting-section">
              <div className="tab-header">
                <h4>PVC 90° Elbow</h4>
                <Button size="sm" onClick={() => addFittingLine('elbow_90')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line
                </Button>
              </div>
              <div className="material-items-list">
                {pvcFittings.elbow_90.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select value={item.pvc_number} onValueChange={(value) => updateFittingLine('elbow_90', index, 'pvc_number', value)}>
                        <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="PVC #" /></SelectTrigger>
                        <SelectContent>
                          {PVC_NUMBERS.map(num => <SelectItem key={num} value={num}>{num}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Input type="number" min="1" step="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateFittingLine('elbow_90', index, 'quantity', e.target.value)} className="w-20 sm:w-24" />
                        <span className="unit-label">pieces</span>
                      </div>
                      {pvcFittings.elbow_90.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeFittingLine('elbow_90', index)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 45° Elbow */}
            <div className="fitting-section">
              <div className="tab-header">
                <h4>PVC 45° Elbow</h4>
                <Button size="sm" onClick={() => addFittingLine('elbow_45')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line
                </Button>
              </div>
              <div className="material-items-list">
                {pvcFittings.elbow_45.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select value={item.pvc_number} onValueChange={(value) => updateFittingLine('elbow_45', index, 'pvc_number', value)}>
                        <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="PVC #" /></SelectTrigger>
                        <SelectContent>
                          {PVC_NUMBERS.map(num => <SelectItem key={num} value={num}>{num}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Input type="number" min="1" step="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateFittingLine('elbow_45', index, 'quantity', e.target.value)} className="w-20 sm:w-24" />
                        <span className="unit-label">pieces</span>
                      </div>
                      {pvcFittings.elbow_45.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeFittingLine('elbow_45', index)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* End Cap */}
            <div className="fitting-section">
              <div className="tab-header">
                <h4>PVC End Cap</h4>
                <Button size="sm" onClick={() => addFittingLine('end_cap')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line
                </Button>
              </div>
              <div className="material-items-list">
                {pvcFittings.end_cap.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select value={item.pvc_number} onValueChange={(value) => updateFittingLine('end_cap', index, 'pvc_number', value)}>
                        <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="PVC #" /></SelectTrigger>
                        <SelectContent>
                          {PVC_NUMBERS.map(num => <SelectItem key={num} value={num}>{num}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Input type="number" min="1" step="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateFittingLine('end_cap', index, 'quantity', e.target.value)} className="w-20 sm:w-24" />
                        <span className="unit-label">pieces</span>
                      </div>
                      {pvcFittings.end_cap.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeFittingLine('end_cap', index)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tee */}
            <div className="fitting-section">
              <div className="tab-header">
                <h4>PVC Tee</h4>
                <Button size="sm" onClick={() => addFittingLine('tee')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line
                </Button>
              </div>
              <div className="material-items-list">
                {pvcFittings.tee.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select value={item.pvc_number} onValueChange={(value) => updateFittingLine('tee', index, 'pvc_number', value)}>
                        <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="PVC #" /></SelectTrigger>
                        <SelectContent>
                          {PVC_NUMBERS.map(num => <SelectItem key={num} value={num}>{num}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Input type="number" min="1" step="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateFittingLine('tee', index, 'quantity', e.target.value)} className="w-20 sm:w-24" />
                        <span className="unit-label">pieces</span>
                      </div>
                      {pvcFittings.tee.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeFittingLine('tee', index)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PVC Victaulic 90° Elbow */}
            <div className="fitting-section">
              <div className="tab-header">
                <h4>PVC Victaulic 90° Elbow</h4>
                <Button size="sm" onClick={() => addFittingLine('victaulic_90')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line
                </Button>
              </div>
              <div className="material-items-list">
                {pvcFittings.victaulic_90.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select value={item.size} onValueChange={(value) => updateFittingLine('victaulic_90', index, 'size', value)}>
                        <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="Size" /></SelectTrigger>
                        <SelectContent>
                          {VICTAULIC_SIZES.map(size => <SelectItem key={size} value={size}>{size}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Input type="number" min="1" step="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateFittingLine('victaulic_90', index, 'quantity', e.target.value)} className="w-20 sm:w-24" />
                        <span className="unit-label">pieces</span>
                      </div>
                      {pvcFittings.victaulic_90.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeFittingLine('victaulic_90', index)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PVC Victaulic 45° Elbow */}
            <div className="fitting-section">
              <div className="tab-header">
                <h4>PVC Victaulic 45° Elbow</h4>
                <Button size="sm" onClick={() => addFittingLine('victaulic_45')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line
                </Button>
              </div>
              <div className="material-items-list">
                {pvcFittings.victaulic_45.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select value={item.size} onValueChange={(value) => updateFittingLine('victaulic_45', index, 'size', value)}>
                        <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="Size" /></SelectTrigger>
                        <SelectContent>
                          {VICTAULIC_SIZES.map(size => <SelectItem key={size} value={size}>{size}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Input type="number" min="1" step="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateFittingLine('victaulic_45', index, 'quantity', e.target.value)} className="w-20 sm:w-24" />
                        <span className="unit-label">pieces</span>
                      </div>
                      {pvcFittings.victaulic_45.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeFittingLine('victaulic_45', index)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PVC Victaulic Tee */}
            <div className="fitting-section">
              <div className="tab-header">
                <h4>PVC Victaulic Tee</h4>
                <Button size="sm" onClick={() => addFittingLine('victaulic_tee')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line
                </Button>
              </div>
              <div className="material-items-list">
                {pvcFittings.victaulic_tee.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select value={item.size} onValueChange={(value) => updateFittingLine('victaulic_tee', index, 'size', value)}>
                        <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="Size" /></SelectTrigger>
                        <SelectContent>
                          {VICTAULIC_SIZES.map(size => <SelectItem key={size} value={size}>{size}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Input type="number" min="1" step="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateFittingLine('victaulic_tee', index, 'quantity', e.target.value)} className="w-20 sm:w-24" />
                        <span className="unit-label">pieces</span>
                      </div>
                      {pvcFittings.victaulic_tee.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeFittingLine('victaulic_tee', index)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PVC Coupling Covers */}
            <div className="fitting-section">
              <div className="tab-header">
                <h4>PVC Coupling Covers</h4>
                <Button size="sm" onClick={() => addFittingLine('coupling_covers')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line
                </Button>
              </div>
              <div className="material-items-list">
                {pvcFittings.coupling_covers.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select value={item.size} onValueChange={(value) => updateFittingLine('coupling_covers', index, 'size', value)}>
                        <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder="Size" /></SelectTrigger>
                        <SelectContent>
                          {COUPLING_COVER_SIZES.map(size => <SelectItem key={size} value={size}>{size}"</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Input type="number" min="1" step="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateFittingLine('coupling_covers', index, 'quantity', e.target.value)} className="w-20 sm:w-24" />
                        <span className="unit-label">pieces</span>
                      </div>
                      {pvcFittings.coupling_covers.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeFittingLine('coupling_covers', index)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Duct Insulation Tab */}
        <TabsContent value="duct">
          <div className="tab-content">
            <div className="tab-header">
              <h3>Duct Insulation</h3>
              <Button onClick={addDuctLine}>
                <Plus className="w-4 h-4 mr-2" />
                Add Line
              </Button>
            </div>

            <div className="material-items-list">
              {ductItems.map((item, index) => (
                <div key={index} className="material-item-box">
                  <div className="item-controls" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <Select value={item.duct_type} onValueChange={(value) => updateDuctLine(index, 'duct_type', value)}>
                      <SelectTrigger className="w-full sm:w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flexible_wrap">Flexible Duct Wrap</SelectItem>
                        <SelectItem value="rigid_board">Rigid Board Insulation</SelectItem>
                        <SelectItem value="rubber_sheet">Rubber Insulation Sheets</SelectItem>
                        <SelectItem value="rubber_roll">Rubber Insulation Roll</SelectItem>
                        <SelectItem value="fw_3m_615">Firewrap - 3M 615 (Ventilation duct)</SelectItem>
                        <SelectItem value="fw_3m_615_plus">Firewrap - 3M 615+ (Kitchen grease duct)</SelectItem>
                        <SelectItem value="fw_cl4">Firewrap - CL4</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select value={item.thickness} onValueChange={(value) => updateDuctLine(index, 'thickness', value)}>
                      <SelectTrigger className="w-full sm:w-32">
                        <SelectValue placeholder="Thickness" />
                      </SelectTrigger>
                      <SelectContent>
                        {(item.duct_type === 'fw_3m_615' || item.duct_type === 'fw_3m_615_plus'
                          ? ['1.5"']
                          : item.duct_type === 'fw_cl4'
                            ? ['1.5"', '3"']
                            : DUCT_THICKNESS
                        ).map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Input 
                        type="number" 
                        min="1"
                        step="1"
                        placeholder="Qty" 
                        value={item.quantity}
                        onChange={(e) => updateDuctLine(index, 'quantity', e.target.value)}
                        className="w-20 sm:w-24"
                      />
                      <span className="unit-label">
                        {item.duct_type === 'flexible_wrap' ? 'Rolls' :
                         item.duct_type === 'rigid_board' ? 'Boxes' :
                         item.duct_type === 'rubber_roll' ? 'Rolls' :
                         (item.duct_type === 'fw_3m_615' || item.duct_type === 'fw_3m_615_plus' || item.duct_type === 'fw_cl4') ? 'Rolls' : 'Sheets'}
                      </span>
                    </div>

                    {ductItems.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeDuctLine(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Jacketing Tab */}
        <TabsContent value="jacketing">
          <div className="tab-content">
            <div className="tab-header">
              <h3>Jacketing Materials</h3>
              <Button onClick={addJacketingLine} disabled={jacketingItems.length >= 20}>
                <Plus className="w-4 h-4 mr-2" />
                Add Line ({jacketingItems.length}/20)
              </Button>
            </div>

            {jacketingItems.length === 0 ? (
              <p className="empty-state">No jacketing items added yet</p>
            ) : (
              <div className="material-items-list">
                {jacketingItems.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select 
                        value={item.type} 
                        onValueChange={(value) => updateJacketingLine(index, 'type', value)}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {JACKETING_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select 
                        value={item.rolls} 
                        onValueChange={(value) => updateJacketingLine(index, 'rolls', value)}
                      >
                        <SelectTrigger className="w-full sm:w-40">
                          <SelectValue placeholder="No. of Rolls" />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLL_OPTIONS.map(num => (
                            <SelectItem key={num} value={num.toString()}>{num} Rolls</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {jacketingItems.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeJacketingLine(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Aluminum Pipe Jacket Section */}
            <div className="tab-header mt-6">
              <h3>Aluminum Pipe Jacket</h3>
              <Button onClick={addAluminumPipeJacketLine} disabled={aluminumPipeJackets.length >= 20}>
                <Plus className="w-4 h-4 mr-2" />
                Add Line ({aluminumPipeJackets.length}/20)
              </Button>
            </div>

            {aluminumPipeJackets.length === 0 ? (
              <p className="empty-state">No aluminum pipe jacket items added yet</p>
            ) : (
              <div className="material-items-list">
                {aluminumPipeJackets.map((item, index) => (
                  <div key={index} className="material-item-box">
                    <div className="item-controls" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                      <Select 
                        value={item.pipeSize} 
                        onValueChange={(value) => updateAluminumPipeJacketLine(index, 'pipeSize', value)}
                      >
                        <SelectTrigger className="w-full sm:w-32">
                          <SelectValue placeholder="Pipe Size" />
                        </SelectTrigger>
                        <SelectContent>
                          {ALUMINUM_PIPE_SIZES.map(size => (
                            <SelectItem key={size} value={size}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select 
                        value={item.thickness} 
                        onValueChange={(value) => updateAluminumPipeJacketLine(index, 'thickness', value)}
                      >
                        <SelectTrigger className="w-full sm:w-32">
                          <SelectValue placeholder="Thickness" />
                        </SelectTrigger>
                        <SelectContent>
                          {ALUMINUM_PIPE_THICKNESS.map(thickness => (
                            <SelectItem key={thickness} value={thickness}>{thickness}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Input
                        placeholder="Notes (optional)"
                        value={item.notes}
                        onChange={(e) => updateAluminumPipeJacketLine(index, 'notes', e.target.value)}
                        className="flex-1"
                      />

                      {aluminumPipeJackets.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeAluminumPipeJacketLine(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="tape">
          <div className="tab-content">
            <h3>Tape & Fasteners</h3>
            
            <div className="material-items-list">
              {/* Silver Tape */}
              <div className="material-item-box">
                <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Label className="item-label" style={{ marginBottom: 0, minWidth: '200px' }}>Silver Tape</Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Input 
                      type="number" 
                      min="1"
                      step="1"
                      placeholder="0"
                      value={tapeItems.silver_tape.quantity || ''}
                      onChange={(e) => setTapeItems({
                        ...tapeItems,
                        silver_tape: { quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                    <span className="unit-label">Rolls</span>
                  </div>
                </div>
              </div>

              {/* Aero Tape */}
              <div className="material-item-box">
                <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Label className="item-label" style={{ marginBottom: 0, minWidth: '200px' }}>Aero Tape</Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Input 
                      type="number" 
                      min="1"
                      step="1"
                      placeholder="0"
                      value={tapeItems.aerp_tape.quantity || ''}
                      onChange={(e) => setTapeItems({
                        ...tapeItems,
                        aerp_tape: { quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                    <span className="unit-label">Rolls</span>
                  </div>
                </div>
              </div>

              {/* Vinyl White Tape */}
              <div className="material-item-box">
                <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Label className="item-label" style={{ marginBottom: 0, minWidth: '200px' }}>Vinyl White Tape</Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Input 
                      type="number" 
                      min="1"
                      step="1"
                      placeholder="0"
                      value={tapeItems.vinyl_white_tape.quantity || ''}
                      onChange={(e) => setTapeItems({
                        ...tapeItems,
                        vinyl_white_tape: { quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                    <span className="unit-label">Rolls</span>
                  </div>
                </div>
              </div>

              {/* Cup Head Pins */}
              <div className="material-item-box">
                <div className="tab-header" style={{ marginBottom: '0.75rem' }}>
                  <Label className="item-label" style={{ marginBottom: 0 }}>Cup Head Pins</Label>
                  <Button size="sm" onClick={() => {
                    const updated = { ...tapeItems };
                    updated.cup_head_pins = [...updated.cup_head_pins, { size: '1"', quantity: 0 }];
                    setTapeItems(updated);
                  }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Line
                  </Button>
                </div>
                {tapeItems.cup_head_pins.map((pin, index) => (
                  <div key={index} className="item-controls" style={{ alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <Select 
                      value={pin.size} 
                      onValueChange={(value) => {
                        const updated = { ...tapeItems };
                        updated.cup_head_pins[index].size = value;
                        setTapeItems(updated);
                      }}
                    >
                      <SelectTrigger className="w-full sm:w-32">
                        <SelectValue placeholder="Size" />
                      </SelectTrigger>
                      <SelectContent>
                        {PIN_SIZES.map(size => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Input 
                        type="number" 
                        min="1"
                        step="1"
                        placeholder="0"
                        value={pin.quantity || ''}
                        onChange={(e) => {
                          const updated = { ...tapeItems };
                          updated.cup_head_pins[index].quantity = parseInt(e.target.value) || 0;
                          setTapeItems(updated);
                        }}
                        className="w-20 sm:w-24"
                      />
                      <span className="unit-label">Boxes</span>
                    </div>

                    {tapeItems.cup_head_pins.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => {
                        const updated = { ...tapeItems };
                        updated.cup_head_pins = updated.cup_head_pins.filter((_, i) => i !== index);
                        setTapeItems(updated);
                      }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Corner Beads */}
              <div className="material-item-box">
                <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Label className="item-label" style={{ marginBottom: 0, minWidth: '200px' }}>Corner Beads</Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Input 
                      type="number" 
                      min="1"
                      step="1"
                      placeholder="0"
                      value={tapeItems.corner_beads.quantity || ''}
                      onChange={(e) => setTapeItems({
                        ...tapeItems,
                        corner_beads: { quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                    <span className="unit-label">Packets</span>
                  </div>
                </div>
              </div>

              {/* Canvas Lagging */}
              <div className="material-item-box">
                <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Label className="item-label" style={{ marginBottom: 0, minWidth: '200px' }}>Canvas Lagging</Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Input 
                      type="number" 
                      min="1"
                      step="1"
                      placeholder="0"
                      value={tapeItems.canvas_lagging.quantity || ''}
                      onChange={(e) => setTapeItems({
                        ...tapeItems,
                        canvas_lagging: { quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                    <span className="unit-label">Litres</span>
                  </div>
                </div>
              </div>

              {/* Rubber Glue */}
              <div className="material-item-box">
                <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Label className="item-label" style={{ marginBottom: 0, minWidth: '200px' }}>Rubber Glue</Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Input 
                      type="number" 
                      min="1"
                      step="1"
                      placeholder="0"
                      value={tapeItems.rubber_glue.quantity || ''}
                      onChange={(e) => setTapeItems({
                        ...tapeItems,
                        rubber_glue: { quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                    <span className="unit-label">Litres</span>
                  </div>
                </div>
              </div>

              {/* Silicon Sealant Outdoor */}
              <div className="material-item-box">
                <div className="item-controls" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Label className="item-label" style={{ marginBottom: 0, minWidth: '200px' }}>Silicon Sealant Outdoor</Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Input 
                      type="number" 
                      min="1"
                      step="1"
                      placeholder="0"
                      value={tapeItems.silicon_sealant.quantity || ''}
                      onChange={(e) => setTapeItems({
                        ...tapeItems,
                        silicon_sealant: { quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                    <span className="unit-label">Tubes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Firestopping Tab */}
        <TabsContent value="firestopping">
          <div className="tab-content">
            <h3>Firestopping Materials</h3>
            
            <div className="firestopping-items-list">
              {/* Self Level Silicon Sealant */}
              <div className="firestopping-item-box">
                <Label className="item-label">Self Level Silicon Sealant</Label>
                <div className="item-controls">
                  <div className="control-group">
                    <Label className="control-label">Type</Label>
                    <Select
                      value={firestoppingItems.self_level_silicon.type}
                      onValueChange={(value) => setFirestoppingItems({
                        ...firestoppingItems,
                        self_level_silicon: { ...firestoppingItems.self_level_silicon, type: value }
                      })}
                    >
                      <SelectTrigger className="w-full sm:w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tubes">Tubes</SelectItem>
                        <SelectItem value="Box">Box</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="control-group">
                    <Label className="control-label">Quantity</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={firestoppingItems.self_level_silicon.quantity || ''}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        self_level_silicon: { ...firestoppingItems.self_level_silicon, quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                  </div>
                  <div className="control-group flex-1">
                    <Label className="control-label">Comments</Label>
                    <Input
                      type="text"
                      placeholder="Add notes..."
                      value={firestoppingItems.self_level_silicon.comments}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        self_level_silicon: { ...firestoppingItems.self_level_silicon, comments: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Wall Silicon Sealant */}
              <div className="firestopping-item-box">
                <Label className="item-label">Wall Silicon Sealant</Label>
                <div className="item-controls">
                  <div className="control-group">
                    <Label className="control-label">Type</Label>
                    <Select
                      value={firestoppingItems.wall_silicon.type}
                      onValueChange={(value) => setFirestoppingItems({
                        ...firestoppingItems,
                        wall_silicon: { ...firestoppingItems.wall_silicon, type: value }
                      })}
                    >
                      <SelectTrigger className="w-full sm:w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tubes">Tubes</SelectItem>
                        <SelectItem value="Box">Box</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="control-group">
                    <Label className="control-label">Quantity</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={firestoppingItems.wall_silicon.quantity || ''}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        wall_silicon: { ...firestoppingItems.wall_silicon, quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                  </div>
                  <div className="control-group flex-1">
                    <Label className="control-label">Comments</Label>
                    <Input
                      type="text"
                      placeholder="Add notes..."
                      value={firestoppingItems.wall_silicon.comments}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        wall_silicon: { ...firestoppingItems.wall_silicon, comments: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Roxul Bag */}
              <div className="firestopping-item-box">
                <Label className="item-label">Roxul Bag</Label>
                <div className="item-controls">
                  <div className="control-group">
                    <Label className="control-label">Quantity</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={firestoppingItems.roxul_bag.quantity || ''}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        roxul_bag: { ...firestoppingItems.roxul_bag, quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                  </div>
                  <div className="control-group flex-1">
                    <Label className="control-label">Comments</Label>
                    <Input
                      type="text"
                      placeholder="Add notes..."
                      value={firestoppingItems.roxul_bag.comments}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        roxul_bag: { ...firestoppingItems.roxul_bag, comments: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Collar */}
              <div className="firestopping-item-box">
                <Label className="item-label">Collar</Label>
                {firestoppingItems.collar.map((item, index) => (
                  <div key={index} className="item-controls">
                    <div className="control-group">
                      <Label className="control-label">Size</Label>
                      <Select
                        value={item.size}
                        onValueChange={(value) => {
                          const updated = [...firestoppingItems.collar];
                          updated[index].size = value;
                          setFirestoppingItems({ ...firestoppingItems, collar: updated });
                        }}
                      >
                        <SelectTrigger className="w-full sm:w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='1.5"'>1.5"</SelectItem>
                          <SelectItem value='2"'>2"</SelectItem>
                          <SelectItem value='3"'>3"</SelectItem>
                          <SelectItem value='4"'>4"</SelectItem>
                          <SelectItem value='6"'>6"</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="control-group">
                      <Label className="control-label">Quantity</Label>
                      <Input
                        type="number"
                        placeholder="0"
                        min="0"
                        value={item.quantity || ''}
                        onChange={(e) => {
                          const updated = [...firestoppingItems.collar];
                          updated[index].quantity = parseInt(e.target.value) || 0;
                          setFirestoppingItems({ ...firestoppingItems, collar: updated });
                        }}
                        className="w-20 sm:w-24"
                      />
                    </div>
                    <div className="control-group flex-1">
                      <Label className="control-label">Comments</Label>
                      <Input
                        type="text"
                        placeholder="Add notes..."
                        value={item.comments}
                        onChange={(e) => {
                          const updated = [...firestoppingItems.collar];
                          updated[index].comments = e.target.value;
                          setFirestoppingItems({ ...firestoppingItems, collar: updated });
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Wrap Strip */}
              <div className="firestopping-item-box">
                <Label className="item-label">Wrap Strip</Label>
                <div className="item-controls">
                  <div className="control-group">
                    <Label className="control-label">Quantity (Boxes)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={firestoppingItems.wrap_strip.quantity || ''}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        wrap_strip: { ...firestoppingItems.wrap_strip, quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                  </div>
                  <div className="control-group flex-1">
                    <Label className="control-label">Comments</Label>
                    <Input
                      type="text"
                      placeholder="Add notes..."
                      value={firestoppingItems.wrap_strip.comments}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        wrap_strip: { ...firestoppingItems.wrap_strip, comments: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Metacaulk Joint Strip */}
              <div className="firestopping-item-box">
                <Label className="item-label">Metacaulk Joint Strip</Label>
                <div className="item-controls">
                  <div className="control-group">
                    <Label className="control-label">Quantity (Boxes)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={firestoppingItems.metacaulk_joint_strip.quantity || ''}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        metacaulk_joint_strip: { ...firestoppingItems.metacaulk_joint_strip, quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                  </div>
                  <div className="control-group flex-1">
                    <Label className="control-label">Comments</Label>
                    <Input
                      type="text"
                      placeholder="Add notes..."
                      value={firestoppingItems.metacaulk_joint_strip.comments}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        metacaulk_joint_strip: { ...firestoppingItems.metacaulk_joint_strip, comments: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Metal Strip */}
              <div className="firestopping-item-box">
                <Label className="item-label">Metal Strip</Label>
                <div className="item-controls">
                  <div className="control-group">
                    <Label className="control-label">Quantity (Boxes)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={firestoppingItems.metal_strip.quantity || ''}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        metal_strip: { ...firestoppingItems.metal_strip, quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                  </div>
                  <div className="control-group flex-1">
                    <Label className="control-label">Comments</Label>
                    <Input
                      type="text"
                      placeholder="Add notes..."
                      value={firestoppingItems.metal_strip.comments}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        metal_strip: { ...firestoppingItems.metal_strip, comments: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Screw */}
              <div className="firestopping-item-box">
                <Label className="item-label">Screw</Label>
                <div className="item-controls">
                  <div className="control-group">
                    <Label className="control-label">Quantity (Boxes)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={firestoppingItems.screw.quantity || ''}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        screw: { ...firestoppingItems.screw, quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                  </div>
                  <div className="control-group flex-1">
                    <Label className="control-label">Comments</Label>
                    <Input
                      type="text"
                      placeholder="Add notes..."
                      value={firestoppingItems.screw.comments}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        screw: { ...firestoppingItems.screw, comments: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Z-Frame */}
              <div className="firestopping-item-box">
                <Label className="item-label">Z-Frame</Label>
                <div className="item-controls">
                  <div className="control-group">
                    <Label className="control-label">Quantity</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      min="0"
                      value={firestoppingItems.z_frame.quantity || ''}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        z_frame: { ...firestoppingItems.z_frame, quantity: parseInt(e.target.value) || 0 }
                      })}
                      className="w-20 sm:w-24"
                    />
                  </div>
                  <div className="control-group flex-1">
                    <Label className="control-label">Comments</Label>
                    <Input
                      type="text"
                      placeholder="Add notes..."
                      value={firestoppingItems.z_frame.comments}
                      onChange={(e) => setFirestoppingItems({
                        ...firestoppingItems,
                        z_frame: { ...firestoppingItems.z_frame, comments: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="other">
          <div className="tab-content">
            <h3>Other Items / Special Requirements</h3>
            <Textarea 
              placeholder="Describe any other materials or special requirements..."
              value={othersNotes}
              onChange={(e) => setOthersNotes(e.target.value)}
              rows={8}
            />
          </div>
        </TabsContent>
      </Tabs>

          <div className="form-actions" style={{ marginTop: '16px' }}>
            <Button variant="outline" onClick={() => setWizardStep(2)}>← Categories</Button>
            <Button onClick={() => setWizardStep(4)} data-testid="wizard-next-3">
              Review Order ({totalItemCount} items) →
            </Button>
          </div>
        </div>
      )}

      {/* ====== STEP 4: Review & Submit ====== */}
      {wizardStep === 4 && (
        <div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '12px' }}>Review Order</h3>
          
          <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
            <p style={{ fontSize: '0.85rem', marginBottom: '4px' }}><strong>Job:</strong> {selectedProject?.name || selectedToProject?.name || 'N/A'}</p>
            <p style={{ fontSize: '0.85rem', marginBottom: '4px' }}><strong>Type:</strong> {orderData.order_type}</p>
            <p style={{ fontSize: '0.85rem', marginBottom: '4px' }}><strong>Delivery:</strong> {orderData.delivery_date}</p>
            {orderData.notes && <p style={{ fontSize: '0.85rem' }}><strong>Notes:</strong> {orderData.notes}</p>}
            <Button variant="link" size="sm" onClick={() => setWizardStep(1)} style={{ padding: 0, height: 'auto', fontSize: '0.8rem' }}>Edit Details</Button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            {materialCategories.map(cat => {
              const count = getCategoryCounts()[cat.key];
              if (count === 0) return null;
              const IconComp = cat.icon;
              return (
                <div key={cat.key} style={{ background: '#f0fdf4', borderRadius: '8px', padding: '10px 12px', border: '1px solid #bbf7d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IconComp className="w-5 h-5" style={{ color: cat.color }} />
                    <span style={{ fontWeight: 600 }}>{cat.label}</span>
                    <span style={{ color: '#16a34a', fontSize: '0.85rem' }}>{count} item{count > 1 ? 's' : ''}</span>
                  </div>
                  <Button variant="link" size="sm" onClick={() => { setActiveMaterialCategory(cat.key); setWizardStep(3); }} style={{ padding: 0, height: 'auto', fontSize: '0.8rem' }}>Edit</Button>
                </div>
              );
            })}
            {totalItemCount === 0 && (
              <p style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>No materials added yet. Go back to add items.</p>
            )}
          </div>

          <p style={{ textAlign: 'center', fontWeight: 600, color: '#1e293b', marginBottom: '12px' }}>
            Total: {totalItemCount} item{totalItemCount !== 1 ? 's' : ''}
          </p>

          <div className="form-actions">
            <Button variant="outline" onClick={() => setWizardStep(2)}>← Back</Button>
            <Button onClick={handleSubmitOrder} disabled={(hideProjectSelect ? false : !orderData.project_id) || totalItemCount === 0} data-testid="submit-order-btn">
              {submitButtonText}
            </Button>
          </div>
        </div>
      )}

      <style jsx>{`
        .material-order-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .order-header {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .form-field {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .material-tabs {
          margin-top: 1rem;
        }

        .tab-content {
          padding: 1.5rem 0;
        }

        .tab-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .tab-header h3 {
          font-size: 1.125rem;
          font-weight: 600;
        }

        .tab-header h4 {
          font-size: 1rem;
          font-weight: 600;
          color: #64748b;
        }

        .items-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .item-row {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr 1fr auto;
          gap: 0.75rem;
          align-items: center;
        }

        .pieces-display {
          background: #f8fafc;
          color: #64748b;
          font-weight: 500;
        }

        .empty-state {
          text-align: center;
          color: #94a3b8;
          padding: 2rem;
          font-style: italic;
        }

        .fitting-section {
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .fitting-section:last-child {
          border-bottom: none;
        }

        .fittings-content {
          max-height: 600px;
          overflow-y: auto;
        }

        .firestopping-items-list,
        .material-items-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: 1rem;
        }

        .firestopping-item-box,
        .material-item-box {
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
          background: #f8fafc;
          transition: all 0.2s;
        }

        .firestopping-item-box:hover,
        .material-item-box:hover {
          border-color: #cbd5e1;
          background: #f1f5f9;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .item-label {
          font-size: 0.95rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.75rem;
          display: block;
        }

        .item-controls {
          display: flex;
          gap: 0.75rem;
          align-items: flex-end;
          flex-wrap: wrap;
          max-width: 100%;
        }

        .control-group {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .control-label {
          font-size: 0.8rem;
          font-weight: 500;
          color: #64748b;
        }
        
        .material-section-title {
          font-size: 1rem;
          font-weight: 600;
          color: #475569;
          margin-bottom: 0.5rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .unit-label {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .quantity-input-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .quantity-input-group input {
          width: 80px;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .tape-items {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .tape-item {
          display: grid;
          grid-template-columns: 200px 1fr;
          gap: 1rem;
          align-items: center;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
        }

        .tape-section {
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
        }

        .jacketing-items {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .jacketing-item {
          display: grid;
          grid-template-columns: 250px 1fr;
          gap: 1rem;
          align-items: center;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
}
